import { useEffect, useState } from 'react';
import { PencilIcon, UserIcon, TrashIcon, CurrencyDollarIcon, EyeIcon, KeyIcon } from '@heroicons/react/24/outline';
import { weeksSinceStart, parseCalendarDate } from '@opentri/shared';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import Avatar from '@/components/ui/Avatar';
import Dropdown from '@/components/ui/Dropdown';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { formatDate, getCurrentWeekOfYear } from '@/lib/date';
import { toaster } from '@/components/ui/toaster';
import { useAdminUsers } from './useAdminUsers';
import { useAdminTrainingPlans } from './useAdminTrainingPlans';
import { useAdminUserPhysio } from './useAdminUserPhysio';
import EditPhysioValues from './physio/EditPhysioValues';
import type { UserProps } from '@/api/usersService';
import type { PhysioValueProps } from '@/api/physioValueService';
import type { AllTrainingPlans } from '@/types/planTypes';

const extractLengthKeyword = (keywords: string[]): string => {
  const valid = ['S', 'M', 'L', 'Ironman'];
  const map: Record<string, string> = { long: 'L', marathon: 'L', short: 'S' };
  for (const k of keywords) if (valid.includes(k)) return k;
  for (const k of keywords) if (map[k]) return map[k];
  return 'S';
};

const iconClass = 'h-4 w-4';

const UsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { plans, plansMap } = useAdminTrainingPlans();
  const { users, isLoading, isSearching, deleteUser, assignPlan, resetLink } = useAdminUsers(searchTerm);

  const [assignUser, setAssignUser] = useState<UserProps | null>(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [nextRunDate, setNextRunDate] = useState('');

  const [physioUser, setPhysioUser] = useState<UserProps | null>(null);
  const [localPhysio, setLocalPhysio] = useState<PhysioValueProps | null>(null);
  const { physioData, updatePhysio } = useAdminUserPhysio(physioUser?.user_id ?? null);

  const [deleteTarget, setDeleteTarget] = useState<UserProps | null>(null);
  const [resetLinkUser, setResetLinkUser] = useState<UserProps | null>(null);

  useEffect(() => {
    if (physioData) setLocalPhysio(physioData);
  }, [physioData]);

  const getObjectiveName = (currentPlan: string | undefined): string => {
    if (!currentPlan) return 'Aucun plan sélectionné';
    return plansMap.get(currentPlan)?.planName ?? 'N.C';
  };

  const getObjectiveEndDate = (user: UserProps): string => {
    if (!user.current_plan) return '-';
    const plan = plansMap.get(user.current_plan);
    if (!plan) return '-';
    if (plan.keywords.includes('GENERAL')) return '-';
    if (!user.plan_start_date || !user.competition_date) return '-';
    return formatDate(user.competition_date);
  };

  const getEffectivePlan = (user: UserProps): AllTrainingPlans | undefined => {
    const planStartDate =
      typeof user.plan_start_date === 'string' ? parseCalendarDate(user.plan_start_date) : user.plan_start_date;
    if (!planStartDate) return undefined;
    const notStartedYet = planStartDate > new Date();
    return notStartedYet
      ? plans.find((p) => p.keywords.includes(user.latest_plan || 'SHORT'))
      : plansMap.get(user.current_plan);
  };

  const guessWeekNumber = (user: UserProps): string => {
    const effectivePlan = getEffectivePlan(user);
    if (!effectivePlan) return '';
    const isBackground = effectivePlan.keywords.includes('GENERAL');
    if (!isBackground && user.competition_date && new Date() > new Date(user.competition_date)) return '';
    const weekNumber = isBackground ? getCurrentWeekOfYear(new Date()) : weeksSinceStart(user.plan_start_date);
    return weekNumber?.toString() ?? '';
  };

  const effectivePlanName = (user: UserProps): string => getEffectivePlan(user)?.planName ?? '-';

  const openAssign = (user: UserProps) => {
    setAssignUser(user);
    setSelectedPlan('');
    setNextRunDate('');
  };

  const handleAssignSubmit = () => {
    if (!assignUser || !selectedPlan) return;
    const plan = plans.find((p) => p.plan_id === selectedPlan);
    if (!plan || !plan.weekCount || plan.weekCount < 1) return;
    assignPlan({
      userId: assignUser.user_id,
      planId: selectedPlan,
      competitionDate: nextRunDate ? new Date(nextRunDate) : new Date(),
      latestPlan: extractLengthKeyword(plan.keywords),
      weekCount: plan.weekCount,
    });
    setAssignUser(null);
  };

  const handleUpdatePhysio = () => {
    if (!physioUser || !localPhysio) return;
    updatePhysio({ userId: physioUser.user_id, data: localPhysio });
    setPhysioUser(null);
  };

  const handleRedirectToStripe = (email: string) => {
    const base = import.meta.env.PROD
      ? 'https://dashboard.stripe.com/search?query='
      : 'https://dashboard.stripe.com/test/search?query=';
    window.open(base + encodeURIComponent(email), '_blank');
  };

  const handleViewPlan = (user: UserProps) => {
    const effectivePlan = getEffectivePlan(user);
    if (!effectivePlan) return;
    const week = guessWeekNumber(user);
    const url = week.length > 0
      ? `/plans/view/${effectivePlan.plan_id}/${week}`
      : `/plans/view/${effectivePlan.plan_id}`;
    window.open(url, '_blank');
  };

  const openResetLink = (user: UserProps) => {
    setResetLinkUser(user);
    resetLink.reset();
  };

  const closeResetLink = () => {
    setResetLinkUser(null);
    resetLink.reset();
  };

  const handleCopyResetLink = async () => {
    if (!resetLink.data) return;
    try {
      await navigator.clipboard.writeText(resetLink.data.url);
      toaster.create({ title: 'Lien copié', type: 'success' });
    } catch {
      toaster.create({ title: 'Copie impossible, sélectionnez le lien manuellement', type: 'error' });
    }
  };

  const menuActions = (user: UserProps) => [
    { label: 'Modifier le plan', icon: <PencilIcon className={iconClass} />, onClick: () => openAssign(user) },
    { label: 'Modifier données physio', icon: <UserIcon className={iconClass} />, onClick: () => setPhysioUser(user) },
    { label: 'Voir le plan', icon: <EyeIcon className={iconClass} />, onClick: () => handleViewPlan(user) },
    { label: 'Lien de réinitialisation', icon: <KeyIcon className={iconClass} />, onClick: () => openResetLink(user) },
    { label: 'Voir sur Stripe', icon: <CurrencyDollarIcon className={iconClass} />, onClick: () => handleRedirectToStripe(user.email) },
    { label: 'Supprimer', icon: <TrashIcon className={iconClass} />, destructive: true, onClick: () => setDeleteTarget(user) },
  ];

  const emptyMessage = isSearching
    ? 'Aucun utilisateur ne correspond à cette recherche'
    : 'Aucun utilisateur';

  return (
    <>
      <PageHeader title="Utilisateurs" description="Les 15 derniers inscrits, ou recherchez par nom / email" />

      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {isSearching ? `Résultats (${users.length})` : 'Derniers inscrits'}
          </p>
          <div className="w-full sm:w-72">
            <Input
              placeholder="Rechercher (min. 3 car.)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {isLoading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Chargement…</p>
        ) : users.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Utilisateur</TH>
                <TH>Inscrit le</TH>
                <TH>Objectif</TH>
                <TH>Date objectif</TH>
                <TH>Plan en cours</TH>
                <TH className="text-center">Semaine</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {users.map((user) => (
                <TR key={user.user_id}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <Avatar src={user.avatar_url} name={user.first_name || user.email} />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                          {user.first_name || '—'}
                        </p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </TD>
                  <TD>{formatDate(user.created_at)}</TD>
                  <TD>{getObjectiveName(user.current_plan)}</TD>
                  <TD>{getObjectiveEndDate(user)}</TD>
                  <TD>{effectivePlanName(user)}</TD>
                  <TD className="text-center">{guessWeekNumber(user) || '-'}</TD>
                  <TD className="text-right">
                    <Dropdown actions={menuActions(user)} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Assign plan modal */}
      <Modal
        open={!!assignUser}
        onClose={() => setAssignUser(null)}
        title="Attribuer un plan d'entraînement"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setAssignUser(null)}>
              Annuler
            </Button>
            <Button onClick={handleAssignSubmit} disabled={!selectedPlan}>
              Appliquer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Date de la compétition"
            type="date"
            min={new Date().toISOString().split('T')[0]}
            value={nextRunDate}
            onChange={(e) => setNextRunDate(e.target.value)}
          />
          <Select label="Plan d'entraînement" value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
            <option value="" disabled>
              — Sélectionner un plan —
            </option>
            {plans.map((plan) => (
              <option key={plan.plan_id} value={plan.plan_id}>
                {plan.planName || 'Sans titre'}
              </option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* Physio modal */}
      <Modal
        open={!!physioUser}
        onClose={() => setPhysioUser(null)}
        title="Modifier les données physiologiques"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setPhysioUser(null)}>
              Annuler
            </Button>
            <Button onClick={handleUpdatePhysio} disabled={!localPhysio}>
              Appliquer
            </Button>
          </>
        }
      >
        {physioUser && localPhysio ? (
          <EditPhysioValues
            user={physioUser}
            data={localPhysio}
            updateData={(newData) =>
              setLocalPhysio((prev) => (prev ? { ...prev, ...newData } : prev))
            }
          />
        ) : (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Chargement…</p>
        )}
      </Modal>

      {/* Password reset link modal */}
      <Modal
        open={!!resetLinkUser}
        onClose={closeResetLink}
        title="Lien de réinitialisation du mot de passe"
        size="md"
        footer={
          resetLink.data ? (
            <>
              <Button variant="outline" onClick={closeResetLink}>
                Fermer
              </Button>
              <Button onClick={handleCopyResetLink}>Copier</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={closeResetLink}>
                Annuler
              </Button>
              <Button
                onClick={() => resetLinkUser && resetLink.mutate(resetLinkUser.user_id)}
                disabled={resetLink.isPending}
              >
                {resetLink.isPending ? 'Génération…' : 'Générer le lien'}
              </Button>
            </>
          )
        }
      >
        {resetLink.data ? (
          <div className="flex flex-col gap-3">
            <Input readOnly value={resetLink.data.url} onFocus={(e) => e.target.select()} />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Valable jusqu'au {new Date(resetLink.data.expiresAt).toLocaleString('fr-FR')}, utilisable une seule fois.
              Connexion ensuite avec <strong>{resetLink.data.authEmail}</strong>.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Génère un lien permettant à <strong>{resetLinkUser?.email}</strong> de définir un nouveau mot de
            passe, à lui transmettre par le canal de votre choix. Fonctionne aussi pour un compte Google / Apple.
          </p>
        )}
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Supprimer cet utilisateur"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (deleteTarget) deleteUser(deleteTarget.user_id);
                setDeleteTarget(null);
              }}
            >
              Supprimer
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Cette action est irréversible. Pensez à mettre à jour l'abonnement sous Stripe.
        </p>
      </Modal>
    </>
  );
};

export default UsersPage;
